import { useCallback, useEffect, useState } from "react";
import contractsV2Service from "../../../../services/contractsV2Service";
import marketDataService from "../../../../services/marketDataService";
import { optionChainsV2Service } from "../../../../services/optionChainsV2Service";
import { ForexRateResult, OptionPriceResult } from "../../../../types/marketData";
import { ActiveContractCompositionEntry } from "../../../../types/productV3";

/**
 * One enriched row per contract in the active composition.
 *
 * `price` is the USD-equivalent price per unit -- already FX-normalized so the
 * min-investment utility (which only sees `price`) can add lot tickets across
 * contracts in different currencies without unit-mixing.
 *
 * `nativePrice` / `currency` / `fxRate` are kept for display and tooltips.
 * `price` is null when either the market-data fetch or the FX lookup for a
 * non-USD contract failed -- such rows are excluded by the utility.
 */
export interface EnrichedBasketEntry {
  conId: number;
  ticker: string;
  name: string;
  weight: number;
  minSize: number | null;
  /** USD-equivalent per-unit price -- null if unavailable or FX missing. */
  price: number | null;
  /** Native per-unit price from the market-data API -- null if unavailable. */
  nativePrice: number | null;
  currency: string;
  /** Currency -> USD rate applied to get `price`. null if not applicable. */
  fxRate: number | null;
  versionId: number;
  /** True when the contract is an option (lot size forced to 1, price scaled by multiplier). */
  isOption: boolean;
  /** Option multiplier applied to the per-unit price (1 for stocks, 100 for standard options). */
  multiplier: number;
}

export interface UseBasketMinAnalyzerResult {
  entries: EnrichedBasketEntry[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

function collectUniqueCurrencies(currencies: string[]): string[] {
  const seen = new Set<string>();
  for (const c of currencies) {
    const upper = (c ?? "").toUpperCase();
    if (upper && upper !== "USD") seen.add(upper);
  }
  return Array.from(seen);
}

function isUsableOptionMultiplier(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function useBasketMinAnalyzer(
  composition: ActiveContractCompositionEntry[] | null,
  optionConIds?: ReadonlySet<number>
): UseBasketMinAnalyzerResult {
  const [entries, setEntries] = useState<EnrichedBasketEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const reload = useCallback(() => setReloadCounter((c) => c + 1), []);

  useEffect(() => {
    if (composition === null) {
      setEntries([]);
      setError(null);
      setLoading(false);
      return;
    }

    const conIds = composition.map((c) => c.conId);
    if (conIds.length === 0) {
      setEntries([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        // Split conIds by type so we call the right metadata + price APIs.
        const stockConIds = conIds.filter((id) => !optionConIds?.has(id));
        const optConIds = conIds.filter((id) => optionConIds?.has(id));

        // Step 1: fetch metadata + prices in parallel (separate APIs per type).
        const [
          stockContracts,
          stockPricesMap,
          optionContractsResult,
          optionPricesMap,
        ] = await Promise.all([
          stockConIds.length > 0
            ? contractsV2Service.findContractsByConIds(stockConIds)
            : Promise.resolve([]),
          stockConIds.length > 0
            ? marketDataService.getStockPrices(stockConIds).catch(() => ({} as Record<number, { price: number }>))
            : Promise.resolve({} as Record<number, { price: number }>),
          optConIds.length > 0
            ? optionChainsV2Service.findOptionContractsByConIds(optConIds).catch(() => ({ optionContracts: [] as { conId: number; symbol: string; optionSymbol: string; multiplier: number; exchange: string }[] }))
            : Promise.resolve({ optionContracts: [] as { conId: number; symbol: string; optionSymbol: string; multiplier: number; exchange: string }[] }),
          optConIds.length > 0
            ? marketDataService.getOptionPrices(optConIds).catch(() => ({} as Record<number, OptionPriceResult>))
            : Promise.resolve({} as Record<number, OptionPriceResult>),
        ]);
        if (cancelled) return;

        // Build unified lookup maps.
        const stockContractMap = new Map(stockContracts.map((c) => [c.conId, c]));
        const optionContractMap = new Map(
          optionContractsResult.optionContracts.map((c) => [c.conId, c])
        );

        // Step 2: resolve FX rates for all non-USD currencies present (stocks only;
        // options are priced in USD via the options API).
        const currencies = collectUniqueCurrencies(
          stockContracts.map((c) => c.currency)
        );
        const fxRatesMap: Record<string, ForexRateResult> =
          currencies.length > 0
            ? await marketDataService.getForexRates(currencies).catch(() => ({} as Record<string, ForexRateResult>))
            : {};
        if (cancelled) return;

        const getFxRate = (currency: string): number | null => {
          const upper = (currency ?? "USD").toUpperCase();
          if (upper === "USD") return 1;
          const entry = fxRatesMap[upper];
          return entry && Number.isFinite(entry.rate) ? entry.rate : null;
        };

        const enriched: EnrichedBasketEntry[] = composition.map((entry) => {
          const isOption = optionConIds?.has(entry.conId) ?? false;

          if (isOption) {
            const opt = optionContractMap.get(entry.conId);
            const optPrice = optionPricesMap[entry.conId];
            const nativePrice = optPrice
              ? (optPrice.close ?? ((optPrice.bid + optPrice.ask) / 2))
              : null;
            const multiplier = opt?.multiplier;
            if (!isUsableOptionMultiplier(multiplier)) {
              throw new Error(
                `Option multiplier is unavailable or invalid for contract ${entry.conId}.`
              );
            }
            const priceUsd = nativePrice != null ? nativePrice * multiplier : null;
            return {
              conId: entry.conId,
              ticker: opt?.symbol ?? `OPT_${entry.conId}`,
              name: opt?.optionSymbol ?? "",
              weight: entry.weight,
              minSize: 1,
              price: priceUsd,
              nativePrice,
              currency: "USD",
              fxRate: 1,
              versionId: entry.versionId,
              isOption: true,
              multiplier,
            };
          }

          const contract = stockContractMap.get(entry.conId);
          const priceEntry =
            stockPricesMap[entry.conId] ??
            stockPricesMap[String(entry.conId) as unknown as number];
          const nativePrice =
            priceEntry && typeof priceEntry.price === "number"
              ? priceEntry.price
              : null;
          const currency = (contract?.currency ?? "USD").toUpperCase();
          const fxRate = getFxRate(currency);
          const priceUsd =
            nativePrice != null && fxRate != null ? nativePrice * fxRate : null;
          return {
            conId: entry.conId,
            ticker: contract?.symbol ?? `CONID_${entry.conId}`,
            name: contract?.longName ?? "",
            weight: entry.weight,
            minSize: contract?.minSize ?? null,
            price: priceUsd,
            nativePrice,
            currency,
            fxRate,
            versionId: entry.versionId,
            isOption: false,
            multiplier: 1,
          };
        });
        setEntries(enriched);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "Failed to load composition data";
        setEntries([]);
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [composition, optionConIds, reloadCounter]);

  return { entries, loading, error, reload };
}
